import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { formatShortDateTime as formatDateTime } from '../utils/timeUtils';
import ApiWebhooks from './ApiWebhooks';
import './api/apiPage.css';

const MAX_KEYS = 5;

const Icon = ({ d, size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);
const ICONS = {
  copy: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
  check: 'M5 13l4 4L19 7',
  eye: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
  eyeOff: 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21',
  refresh: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  trash: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  ext: 'M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14',
  plus: 'M12 4v16m8-8H4',
};

function CopyButton({ text, label }) {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch (_) {
      const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (__) { /* ignore */ } document.body.removeChild(ta);
    }
    setDone(true); setTimeout(() => setDone(false), 1800);
  };
  return (
    <button type="button" onClick={copy} className={`apg-btn${done ? ' done' : ''}`}>
      <Icon d={done ? ICONS.check : ICONS.copy} /> {done ? t('apiAccess.copied', 'Copied') : (label || t('apiAccess.copy', 'Copy'))}
    </button>
  );
}

function KeyRow({ k, onChanged, showOwner }) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(null);
  const [confirm, setConfirm] = useState(null); // 'regenerate' | 'revoke'
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const own = !showOwner || k.mine;

  const reveal = async () => {
    if (revealed) { setRevealed(null); return; }
    setErr('');
    try { const r = await axios.get(`/api/v1/keys/${k.id}/reveal`); setRevealed(r.data.key); }
    catch (e) { setErr(e.response?.data?.error?.message || t('apiAccess.revealFailed', 'Could not reveal this key')); }
  };
  const run = async () => {
    setBusy(true); setErr('');
    try {
      if (confirm === 'regenerate') {
        const r = await axios.post(`/api/v1/keys/${k.id}/regenerate`);
        setRevealed(r.data.key);
      } else {
        await axios.delete(`/api/v1/keys/${k.id}`);
      }
      setConfirm(null);
      onChanged();
    } catch (e) {
      setErr(e.response?.data?.error?.message || t('apiAccess.actionFailed', 'Action failed'));
    } finally { setBusy(false); }
  };

  return (
    <div className="apg-row">
      <div className="apg-row-top">
        <div className="apg-row-main">
          <div className="apg-row-name">
            <span>{k.name}</span>
            {showOwner && k.owner && <span className="owner">{k.owner.name || `${k.owner.source}#${k.owner.id}`}</span>}
          </div>
          <div className="apg-row-key">{revealed || k.masked}</div>
          <div className="apg-row-meta">
            <span>{t('apiAccess.created', 'Created')} {k.created_at ? formatDateTime(k.created_at) : '—'}</span>
            <span>
              {k.last_used_at
                ? t('apiAccess.lastUsed', 'Last used {{when}}', { when: formatDateTime(k.last_used_at) }) + (k.last_used_ip ? ` (${k.last_used_ip})` : '')
                : t('apiAccess.neverUsed', 'Never used')}
            </span>
          </div>
        </div>
        <div className="apg-actions">
          {own && (
            <button type="button" onClick={reveal} className="apg-btn">
              <Icon d={revealed ? ICONS.eyeOff : ICONS.eye} /> {revealed ? t('apiAccess.hide', 'Hide') : t('apiAccess.reveal', 'Reveal')}
            </button>
          )}
          {own && revealed && <CopyButton text={revealed} />}
          {own && <button type="button" onClick={() => setConfirm('regenerate')} className="apg-btn warn"><Icon d={ICONS.refresh} /> {t('apiAccess.regenerate', 'Regenerate')}</button>}
          <button type="button" onClick={() => setConfirm('revoke')} className="apg-btn danger"><Icon d={ICONS.trash} /> {t('apiAccess.revoke', 'Revoke')}</button>
        </div>
      </div>
      {confirm && (
        <div className="apg-confirm">
          <span className={`apg-confirm-text${confirm === 'revoke' ? ' bad' : ''}`}>
            {confirm === 'revoke'
              ? t('apiAccess.revokeWarning', 'The key stops working immediately. Integrations using it will get 401.')
              : t('apiAccess.regenerateWarning', 'The current key stops working immediately. Update your integrations with the new one.')}
          </span>
          <button type="button" disabled={busy} onClick={run} className={`apg-btn ${confirm === 'revoke' ? 'danger' : 'warn'}`}>
            {busy ? t('common.loading', 'Loading...') : t('apiAccess.confirm', 'Confirm')}
          </button>
          <button type="button" disabled={busy} onClick={() => setConfirm(null)} className="apg-btn">{t('common.cancel', 'Cancel')}</button>
        </div>
      )}
      {err && <div className="apg-error">{err}</div>}
    </div>
  );
}

export default function ApiAccess() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuper = user && (user.role === 'super_admin' || user.role === 'admin');
  const [keys, setKeys] = useState([]);
  const [allKeys, setAllKeys] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [error, setError] = useState('');

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const base = `${origin}/api/v1`;

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await axios.get('/api/v1/keys');
      setKeys(r.data.data || []);
      if (isSuper && showAll) {
        const a = await axios.get('/api/v1/keys', { params: { all: 1 } });
        setAllKeys((a.data.data || []).filter(k => !k.mine));
      }
    } catch (e) {
      setError(e.response?.data?.error?.message || t('apiAccess.loadFailed', 'Failed to load API keys'));
    } finally { setLoading(false); }
  }, [isSuper, showAll, t]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true); setError('');
    try {
      const r = await axios.post('/api/v1/keys', { name: name.trim() });
      setCreated(r.data);
      setName('');
      load();
    } catch (err) {
      setError(err.response?.data?.error?.message || t('apiAccess.createFailed', 'Failed to create API key'));
    } finally { setCreating(false); }
  };

  const atLimit = keys.length >= MAX_KEYS;
  const sampleKey = created ? created.key : 'flx_live_…';

  return (
    <div className="apg-page">
      <div>
        <h1 className="apg-title">{t('apiAccess.title', 'API')}</h1>
        <p className="apg-lead">
          {t('apiAccess.subtitle', 'Read live match data from your own tools (overlays, bots, analyst desks) and automate the panel with a personal API key.')}
        </p>
      </div>

      {/* Links */}
      <section className="apg-sec">
        <div className="apg-sec-head"><h2>{t('apiAccess.docsTitle', 'Documentation & tools')}</h2></div>
        <p className="apg-hint">{t('apiAccess.docsHint', 'The API tester runs real requests against this panel. The docs cover every endpoint, event type and the migration from the legacy API.')}</p>
        <div className="apg-line">
          <a href="/api-tester" target="_blank" rel="noreferrer" className="apg-btn primary"><Icon d={ICONS.ext} /> {t('apiAccess.openTester', 'Open API tester')}</a>
          <a href="/api/docs" target="_blank" rel="noreferrer" className="apg-btn"><Icon d={ICONS.ext} /> {t('apiAccess.openDocs', 'Open API docs')}</a>
          <a href="/api/docs.md" target="_blank" rel="noreferrer" className="apg-btn"><Icon d={ICONS.ext} /> {t('apiAccess.downloadDocs', 'Docs as Markdown')}</a>
        </div>
        <div className="apg-cmd-block">
          <span className="apg-label">{t('apiAccess.baseUrl', 'Base URL')}</span>
          <div className="apg-cmd">
            <pre className="apg-code">{base}</pre>
            <CopyButton text={base} />
          </div>
        </div>
      </section>

      {/* Keys */}
      <section className="apg-sec">
        <div className="apg-sec-head">
          <h2>{t('apiAccess.keysTitle', 'Your API keys')}</h2>
          <span className="apg-sec-meta">{t('apiAccess.keysCount', '{{count}} of {{max}} used', { count: keys.length, max: MAX_KEYS })}</span>
        </div>
        <p className="apg-hint">
          {t('apiAccess.keysHint', 'A key acts as you: same role and the same matches, teams and servers you can manage. Send it as "Authorization: Bearer <key>" or "X-API-Key: <key>". Treat it like a password.')}
        </p>

        <form onSubmit={create} className="apg-form">
          <input
            className="apg-input"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={60}
            placeholder={t('apiAccess.namePlaceholder', 'Key name, e.g. "Broadcast overlay"')}
            aria-label={t('apiAccess.namePlaceholder', 'Key name, e.g. "Broadcast overlay"')}
            disabled={atLimit}
          />
          <button type="submit" disabled={creating || atLimit || !name.trim()} className="apg-btn primary">
            <Icon d={ICONS.plus} /> {creating ? t('common.loading', 'Loading...') : t('apiAccess.createKey', 'Create key')}
          </button>
        </form>
        {atLimit && <div className="apg-warn-text">{t('apiAccess.limitReached', 'You have reached the maximum of {{max}} keys. Revoke one to create another.', { max: MAX_KEYS })}</div>}

        {created && (
          <div className="apg-note">
            <div className="apg-note-title">{t('apiAccess.createdTitle', 'Key "{{name}}" created', { name: created.name })}</div>
            <div className="apg-cmd">
              <pre className="apg-code">{created.key}</pre>
              <CopyButton text={created.key} />
            </div>
            <div className="apg-small">{t('apiAccess.createdHint', 'You can reveal it again later from this page.')}</div>
          </div>
        )}

        {error && <div className="apg-error">{error}</div>}
        {loading ? (
          <div className="apg-empty">{t('common.loading', 'Loading...')}</div>
        ) : keys.length === 0 ? (
          <div className="apg-empty">{t('apiAccess.noKeys', 'No API keys yet.')}</div>
        ) : (
          <div className="apg-list">{keys.map(k => <KeyRow key={k.id} k={k} onChanged={load} />)}</div>
        )}

        {isSuper && (
          <>
            <label className="apg-check">
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
              {t('apiAccess.showAll', "Show other admins' keys (super admin)")}
            </label>
            {showAll && allKeys && (
              allKeys.length === 0
                ? <div className="apg-empty">{t('apiAccess.noOtherKeys', 'No other admin has an API key.')}</div>
                : <div className="apg-list">{allKeys.map(k => <KeyRow key={k.id} k={k} onChanged={load} showOwner />)}</div>
            )}
          </>
        )}
      </section>

      {/* Quick start */}
      <section className="apg-sec">
        <div className="apg-sec-head"><h2>{t('apiAccess.quickStartTitle', 'Quick start')}</h2></div>
        <p className="apg-hint">{t('apiAccess.quickStartHint', 'Public match data needs no key. A key gives you undelayed data for your own matches, higher rate limits and access to the admin endpoints.')}</p>
        {[
          { label: t('apiAccess.qsCurrent', 'Live matches'), cmd: `curl ${base}/matches/current` },
          { label: t('apiAccess.qsState', 'Match state'), cmd: `curl -H "Authorization: Bearer ${sampleKey}" ${base}/matches/<id>` },
          { label: t('apiAccess.qsStream', 'Live event stream (whole match, then live)'), cmd: `curl -N -H "Authorization: Bearer ${sampleKey}" "${base}/matches/<id>/events/stream?since=0"` },
          { label: t('apiAccess.qsPoll', 'Poll events'), cmd: `curl -H "Authorization: Bearer ${sampleKey}" "${base}/matches/<id>/events?since=<last_id>"` },
        ].map(q => (
          <div key={q.label} className="apg-cmd-block">
            <span className="apg-label">{q.label}</span>
            <div className="apg-cmd">
              <pre className="apg-code">{q.cmd}</pre>
              <CopyButton text={q.cmd} />
            </div>
          </div>
        ))}
        <p className="apg-hint">
          {t('apiAccess.matchTokensHint', 'Per-match links (coach timeout tablets, raw log stream) are on each match page.')}
        </p>
      </section>

      <ApiWebhooks />
    </div>
  );
}
